import { useState } from "react";
import { Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Container, PageHero, PrimaryLink, OutlineLink, CtaBand } from "./_ui";
import { Seo } from "@/components/marketing/Seo";
import { RegisterDialog } from "@/components/marketing/RegisterDialog";

const PLANS = [
  { name: "Månedlig", price: "199,-", unit: "/mnd", note: "per selskap, eks. mva", register: true, cta: "Kom i gang", highlight: false },
  { name: "Årlig", price: "1 990,-", unit: "/år", note: "per selskap, eks. mva — to måneder gratis", register: true, cta: "Kom i gang", highlight: true },
  { name: "Konsern", price: "Ta kontakt", unit: "", note: "Rabatt på datterselskaper og felles porteføljeoversikt", href: "mailto:post@murphy.no?subject=Murphy%20Styrerom%20-%20konsern", cta: "Snakk med oss", highlight: false },
];

const INCLUDED = [
  { title: "Møter", items: ["Smarte møter", "Maler", "Oppgaver og oppfølging", "Skriveassistent", "Digital avstemning", "Finn møtetidspunkt", "Generalforsamling", "Saksforslag", "Kalenderintegrasjon", "Årsoversikt og årsplanlegger", "Fullmakter", "Videomøter", "Transkribering av møter", "Presentasjonsmodus"] },
  { title: "AI-assistent", items: ["AI-assistent i styrerommet", "Automatisk referat", "Hukommelse på tvers av møter", "AI-baserte saksforslag"] },
  { title: "Dokumenter", items: ["Dokumentarkiv", "Samarbeid på dokumenter", "Versjonshistorikk", "Smartmapper", "Deling med eksterne aktører", "Deling på tvers av selskaper", "Detaljert tilgangsstyring", "Ubegrenset lagring", "Private notater", "Møtepakke", "Annotering i PDF", "Datarom"] },
  { title: "Signering", items: ["Digital signering", "Signering med BankID", "Automatiske påminnelser", "Dokumentsynlighet", "Signeringsportal"] },
  { title: "Kommunikasjon", items: ["SMS", "E-post", "Meldingssenter", "Diskusjoner", "Grupper", "Investoroppdateringer"] },
  { title: "Aksjonærer og transaksjoner", items: ["Aksjeeierbok", "Aksjonærregisteroppgave", "Generalforsamling", "Registrer transaksjoner", "Aksjekjøpsavtaler", "Aksjeklasser", "Fullmakter", "Pantsettinger", "Import fra VPS", "Import av aksjeeierbok"] },
  { title: "Rapportering", items: ["Likviditetsprognoser", "Banksaldo", "Balanserapporter", "Regnskapsintegrasjon", "Statistikk"] },
  { title: "Sikkerhet", items: ["Kryptert lagring", "Single sign-on (SSO)", "Tofaktorautentisering", "Avanserte sikkerhetsfunksjoner", "Tilgangslogg"] },
  { title: "Evalueringer", items: ["Styreevaluering", "Daglig leder-evaluering", "Maler for styreevaluering", "Egne spørreundersøkelser"] },
  { title: "Support", items: ["Chat", "E-post", "Telefon", "Hjelpesenter", "Opplæring og kurs"] },
];

const FAQ = [
  { q: "Er prisen per bruker eller per styre?", a: "Per selskap. Dere betaler 199 kr i måneden uansett hvor mange som sitter i styret, og alle funksjoner er inkludert." },
  { q: "Hva koster årsabonnement?", a: "1 990 kr i året per selskap — det tilsvarer to måneder gratis sammenlignet med månedlig betaling." },
  { q: "Er det bindingstid?", a: "Nei. Du kan si opp når som helst, og vi hjelper deg med å eksportere dokumenter og aksjeeierbok." },
  { q: "Hva med flere selskaper?", a: "Alle selskapene samles i én porteføljeoversikt. Ta kontakt så setter vi opp konsernpris for mange selskaper." },
  { q: "Kan vi betale med faktura?", a: "Ja, vi sender faktura på EHF eller e-post. Alle priser er eksklusiv mva." },
];

export default function Priser() {
  const [regOpen, setRegOpen] = useState(false);
  const [regPlan, setRegPlan] = useState("");
  const openRegister = (plan) => { setRegPlan(plan); setRegOpen(true); };
  return (
    <>
      <Seo title="Priser" description="Én pris for hele styret: 199 kr per måned eller 1 990 kr per år per selskap. Alt inkludert — møter, dokumenter, signering, aksjeeierbok, nøkkeltall og AI." path="/priser" />
      <PageHero label="Priser" title="Én pris for hele styret.">
        Samme pris uansett hvor mange styremedlemmer dere er, og uansett hvilke funksjoner dere bruker. Ingen skjulte kostnader og ingen bindingstid.
      </PageHero>

      <Container className="py-10">
        <div className="grid gap-5 lg:grid-cols-3">
          {PLANS.map((p) => (
            <div key={p.name} className={`relative rounded-2xl border p-8 ${p.highlight ? "border-neutral-900 bg-neutral-950 text-white" : "border-neutral-200 bg-white text-neutral-900"}`} data-testid={`plan-${p.name.toLowerCase()}`}>
              {p.highlight ? (
                <span className="absolute right-6 top-6 rounded-full bg-[#CDF546] px-3 py-1 text-xs font-bold text-neutral-900">Anbefalt</span>
              ) : null}
              <p className={`text-sm font-semibold ${p.highlight ? "text-neutral-300" : "text-neutral-500"}`}>{p.name}</p>
              <p className="mt-4 font-headings text-4xl font-extrabold tracking-tight">
                {p.price}<span className={`ml-1 text-base font-semibold ${p.highlight ? "text-neutral-400" : "text-neutral-400"}`}>{p.unit}</span>
              </p>
              <p className={`mt-2 text-sm ${p.highlight ? "text-neutral-400" : "text-neutral-500"}`}>{p.note}</p>
              <div className="mt-6">
                {p.register ? (
                  <button
                    type="button"
                    data-testid={`plan-cta-${p.name.toLowerCase()}`}
                    onClick={() => openRegister(p.name)}
                    className={
                      p.highlight
                        ? "inline-flex w-full items-center justify-center rounded-full bg-[#CDF546] px-6 py-3 text-sm font-semibold text-neutral-900 transition-opacity hover:opacity-90"
                        : "inline-flex w-full items-center justify-center rounded-full border border-neutral-300 bg-white px-6 py-3 text-sm font-semibold text-neutral-900 transition-colors hover:border-neutral-900"
                    }
                  >
                    {p.cta}
                  </button>
                ) : p.highlight ? (
                  <a href={p.href} data-testid={`plan-cta-${p.name.toLowerCase()}`} className="inline-flex w-full items-center justify-center rounded-full bg-[#CDF546] px-6 py-3 text-sm font-semibold text-neutral-900 transition-opacity hover:opacity-90">{p.cta}</a>
                ) : (
                  <OutlineLink href={p.href} testid={`plan-cta-${p.name.toLowerCase()}`} className="w-full">{p.cta}</OutlineLink>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-neutral-500">Alle priser er eksklusiv mva. Abonnementet faktureres på EHF eller e-post.</p>
      </Container>

      <Container className="py-12 sm:py-16">
        <h2 className="font-headings text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Alt er inkludert. Hele tiden.</h2>
        <p className="mt-3 max-w-2xl text-base text-neutral-600">Ingen moduler å kjøpe i tillegg, ingen funksjoner låst bak et dyrere nivå.</p>
        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {INCLUDED.map((g) => (
            <div key={g.title}>
              <h3 className="font-headings text-base font-bold text-neutral-900">{g.title}</h3>
              <ul className="mt-3 space-y-1.5 text-sm text-neutral-600">
                {g.items.map((it) => (
                  <li key={it} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#9bb800]" /> {it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Container>

      <Container className="py-12 sm:py-16">
        <h2 className="font-headings text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Spørsmål og svar</h2>
        <Accordion type="single" collapsible className="mt-8 max-w-3xl">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left font-headings text-base font-bold text-neutral-900 hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-neutral-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Container>

      <Container className="pb-8">
        <div className="flex flex-wrap gap-3">
          <PrimaryLink href="mailto:post@murphy.no?subject=Demo%20av%20Murphy%20Styrerom">Book demo</PrimaryLink>
          <OutlineLink to="/funksjoner">Se all funksjonalitet</OutlineLink>
        </div>
      </Container>

      <CtaBand />
      <RegisterDialog open={regOpen} onOpenChange={setRegOpen} plan={regPlan} />
    </>
  );
}
